// ProblemSection.js - Version sans animations au scroll
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MdCheckCircle, 
  MdArrowForward, 
  MdFlashOn, 
  MdAutoAwesome,
  MdRocketLaunch,
  MdStars,
  MdVerified,
  MdInsights,
  MdTimeline,
  MdWarning,
  MdErrorOutline,
  MdAccessTime,
  MdChat,
  MdPhonelink,
  MdShare,
  MdDashboard,
  MdTrendingDown
} from 'react-icons/md';
import { 
  FaBrain, 
  FaUsers, 
  FaChartLine, 
  FaDatabase, 
  FaRobot, 
  FaMagic, 
  FaCrown,
  FaInfinity,
  FaWhatsapp,
  FaEnvelope,
  FaPhoneAlt,
  FaHashtag
} from 'react-icons/fa';
import { GiAstronautHelmet, GiGalaxy, GiStarsStack, GiDiamondHard, GiCrystalBall, GiSpiralShell } from 'react-icons/gi';
import './ProblemSection.css';

const ProblemSection = () => {
  const [expandedProblem, setExpandedProblem] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isCursorActive, setIsCursorActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  let cursorTimeout;

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
      
      clearTimeout(cursorTimeout);
      cursorTimeout = setTimeout(() => setIsCursorActive(false), 100);
    };
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(cursorTimeout);
    };
  }, []);

  const problems = [
    {
      id: 1,
      icon: <MdErrorOutline />,
      problem: "Vous oubliez de relancer certains prospects",
      problemDesc: "Des opportunités prometteuses tombent à l'eau simplement parce qu'aucune relance n'a été programmée.",
      solution: "On met en place un système de relance automatique qui s'occupe de tous vos prospects — aucun oubli possible.",
      problemColor: "#ef4444",
      solutionColor: "#3B82F6",
      impact: "Prospects perdus • Chiffre d'affaires en baisse • Opportunités manquées",
      result: "Taux de conversion x2 • 0 prospect oublié • Revenus prévisibles",
      stats: ["+45% conversion", "0 oubli"],
      gradient: "linear-gradient(135deg, #ef4444, #dc2626)"
    },
    {
      id: 2,
      icon: <MdAccessTime />,
      problem: "Vous manquez de temps pour communiquer régulièrement",
      problemDesc: "Entre les urgences quotidiennes, la communication avec vos prospects passe au second plan.",
      solution: "On automatise votre communication régulière — vos prospects reçoivent les bons messages au bon moment, sans que vous ayez à y penser.",
      problemColor: "#f59e0b",
      solutionColor: "#3B82F6",
      impact: "Relation client dégradée • Opportunités qui refroidissent • Manque de suivi",
      result: "Communication fluide • Présence constante • Gain de temps libéré",
      stats: ["-15h/semaine", "+200% réactivité"],
      gradient: "linear-gradient(135deg, #f59e0b, #d97706)"
    },
    {
      id: 3,
      icon: <MdWarning />,
      problem: "Vos réponses arrivent trop tard",
      problemDesc: "Quand un prospect vous contacte, il attend une réponse rapide. Chaque heure qui passe fait fondre son intérêt.",
      solution: "On déploie des notifications en temps réel et des réponses automatisées — plus jamais de réponse tardive.",
      problemColor: "#ef4444",
      solutionColor: "#3B82F6",
      impact: "Prospects qui partent chez la concurrence • Image négative • Vente perdue",
      result: "Réponse instantanée • Taux de closing boosté • Image premium",
      stats: ["-90% temps réponse", "+60% closing"],
      gradient: "linear-gradient(135deg, #ef4444, #dc2626)"
    },
    {
      id: 4,
      icon: <MdShare />,
      problem: "Vos contacts sont dispersés entre WhatsApp, email, téléphone et réseaux sociaux",
      problemDesc: "Les informations sont partout. Chaque outil a sa conversation, et vous perdez le fil.",
      solution: "On centralise tous vos canaux dans un seul endroit — vous voyez toutes vos conversations au même endroit.",
      problemColor: "#ef4444",
      solutionColor: "#3B82F6",
      impact: "Perte d'infos critiques • Doublons • Suivi impossible",
      result: "Source de vérité unique • Vision 360° • Collaboration fluide",
      stats: ["100% centralisé", "+150% productivité"],
      gradient: "linear-gradient(135deg, #ef4444, #dc2626)"
    },
    {
      id: 5,
      icon: <MdDashboard />,
      problem: "Vous n'avez pas de système clair pour suivre vos opportunités",
      problemDesc: "Chaque prospect est suivi différemment. Impossible de savoir où en est votre pipeline commercial.",
      solution: "On installe un CRM simple adapté à votre activité — vous voyez d'un coup d'œil toutes vos opportunités.",
      problemColor: "#ef4444",
      solutionColor: "#3B82F6",
      impact: "Opportunités qui stagnent • Prévisions impossibles • Chaos commercial",
      result: "Pipeline clair • Prévisions fiables • Scalabilité assurée",
      stats: ["+35% visibilité", "Croissance garantie"],
      gradient: "linear-gradient(135deg, #ef4444, #dc2626)"
    }
  ];

  const toggleExpand = (id) => {
    setExpandedProblem(expandedProblem === id ? null : id);
  };

  return (
    <section className="ps-section-root" ref={sectionRef}>
      <motion.div 
        className={`ps-cursor-glow ${isCursorActive ? 'active' : ''}`}
        animate={{
          x: mousePosition.x - 250,
          y: mousePosition.y - 250,
        }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 200,
          mass: 0.5
        }}
      />

      <div className="ps-bg-gradient-animation"></div>
      <div className="ps-grid-pattern"></div>
      
      <div className="ps-particles-container">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="ps-particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 18}s`,
              animationDuration: `${18 + Math.random() * 25}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
            }}
          />
        ))}
      </div>

      <div className="ps-light-orbs">
        <div className="ps-orb ps-orb-1" style={{ transform: `translate(${scrollProgress * 80}px, ${scrollProgress * 40}px)` }} />
        <div className="ps-orb ps-orb-2" style={{ transform: `translate(-${scrollProgress * 60}px, -${scrollProgress * 50}px)` }} />
        <div className="ps-orb ps-orb-3" style={{ transform: `translate(${scrollProgress * 40}px, -${scrollProgress * 30}px)` }} />
      </div>

      <div className="ps-floating-elements">
        <GiGalaxy className="ps-floating ps-floating-1" />
        <GiSpiralShell className="ps-floating ps-floating-2" />
        <GiStarsStack className="ps-floating ps-floating-3" />
      </div>

      <div className="ps-container">
        <div className="ps-header">
          <motion.div 
            className="ps-badge-wrapper"
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            <div className="ps-badge">
              <GiCrystalBall className="ps-badge-icon" />
              <span>Problèmes → Solutions</span>
              <MdAutoAwesome className="ps-badge-icon" />
            </div>
          </motion.div>
          
          <h2 className="ps-title">
            <span className="ps-title-line">
              <span className="ps-title-gradient">Vous perdez peut-être</span> des clients
            </span>
            <span className="ps-title-line">
              <span className="ps-title-highlight">sans le savoir.</span>
            </span>
            <div className="ps-title-underline"></div>
          </h2>
          
          <p className="ps-subtitle">
            Chaque semaine, des prospects intéressés passent à côté de votre entreprise 
            <br />parce qu'ils ne sont pas <span className="ps-highlight-text">suivis correctement</span>.
          </p>
        </div>

        <div className="ps-grid">
          {problems.map((item, index) => (
            <div
              key={item.id}
              className={`ps-card ${expandedProblem === item.id ? 'ps-expanded' : ''}`}
              onMouseEnter={() => setHoveredCard(item.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="ps-card-border-glow" style={{ background: `linear-gradient(90deg, ${item.solutionColor}, ${item.solutionColor}60, transparent)` }} />
              <div 
                className="ps-card-hover-overlay" 
                style={{ 
                  background: `radial-gradient(circle at 30% 20%, ${item.solutionColor}08, transparent 80%)`,
                }}
              />
              
              <div 
                className="ps-card-inner"
                onClick={() => toggleExpand(item.id)}
              >
                <div className="ps-card-bg-icons">
                  <MdStars className="ps-bg-icon" style={{ color: item.solutionColor, fontSize: '2.5rem', top: '8%', right: '3%' }} />
                  <GiDiamondHard className="ps-bg-icon" style={{ color: item.solutionColor, fontSize: '1.8rem', bottom: '5%', left: '2%' }} />
                </div>

                <div className="ps-problem-side">
                  <motion.div 
                    className="ps-icon-wrapper"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="ps-icon" style={{ color: item.problemColor }}>
                      {item.icon}
                    </div>
                  </motion.div>
                  
                  <div className="ps-problem-label">
                    <span className="ps-label-icon">⚠️</span>
                    <span>PROBLÈME RÉCURRENT</span>
                  </div>
                  
                  <h3 className="ps-problem-question">{item.problem}</h3>
                  <p className="ps-problem-description">{item.problemDesc}</p>
                  
                  <div className="ps-impact-box">
                    <div className="ps-impact-header">
                      <MdFlashOn className="ps-impact-icon" style={{ color: item.problemColor }} />
                      <span className="ps-impact-label">Conséquences</span>
                    </div>
                    <div className="ps-impact-tags">
                      {item.impact.split(' • ').map((impact, idx) => (
                        <span key={idx} className="ps-impact-tag">
                          {impact}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {expandedProblem !== item.id && (
                  <div className="ps-stats-preview">
                    {item.stats.map((stat, idx) => (
                      <motion.div 
                        key={idx} 
                        className="ps-stat-pill"
                        whileHover={{ x: 3 }}
                      >
                        <MdAutoAwesome style={{ color: item.solutionColor }} />
                        <span>{stat}</span>
                      </motion.div>
                    ))}
                  </div>
                )}

                <AnimatePresence>
                  {expandedProblem === item.id && (
                    <motion.div 
                      className="ps-solution-side"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: [0.2, 0.9, 0.4, 1] }}
                    >
                      <div className="ps-solution-header">
                        <div className="ps-solution-label">
                          <span className="ps-label-icon">✨</span>
                          <span>NOTRE SOLUTION</span>
                        </div>
                        <motion.div 
                          className="ps-magic-icon"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                        >
                          <FaMagic style={{ color: item.solutionColor }} />
                        </motion.div>
                      </div>
                      
                      <p className="ps-solution-description">{item.solution}</p>
                      
                      <div className="ps-result-box">
                        <div className="ps-result-header">
                          <FaCrown className="ps-result-crown" style={{ color: item.solutionColor }} />
                          <span className="ps-result-label">CE QUE VOUS OBTENEZ</span>
                        </div>
                        <div className="ps-result-tags">
                          {item.result.split(' • ').map((result, idx) => (
                            <span key={idx} className="ps-result-tag">
                              <MdVerified style={{ color: item.solutionColor }} />
                              {result}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="ps-stats-grid">
                        {item.stats.map((stat, idx) => (
                          <motion.div 
                            key={idx} 
                            className="ps-stat-card"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.08 }}
                            whileHover={{ y: -2 }}
                          >
                            <GiDiamondHard className="ps-stat-gem" style={{ color: item.solutionColor }} />
                            <span className="ps-stat-value">{stat}</span>
                          </motion.div>
                        ))}
                      </div>

                      <motion.button 
                        className="ps-solution-cta"
                        style={{ background: `linear-gradient(135deg, ${item.solutionColor}, ${item.solutionColor}cc)` }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>Résoudre ce problème</span>
                        <MdRocketLaunch className="ps-cta-icon" />
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="ps-expand-indicator">
                  <motion.div 
                    className="ps-indicator-button"
                    animate={{ rotate: expandedProblem === item.id ? 180 : 0 }}
                  >
                    <span className="ps-indicator-text">
                      {expandedProblem === item.id ? 'Réduire' : 'Découvrir la solution'}
                    </span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </motion.div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Phrase forte entre les cartes et la CTA */}
        <div className="ps-strong-message">
          <div className="ps-strong-message-inner">
            <div className="ps-strong-icon">
              <MdWarning />
            </div>
            <p className="ps-strong-text">
              <span>Le problème n'est pas toujours le manque de prospects.</span>
              <span className="ps-strong-highlight"> Le vrai problème, c'est souvent le manque de suivi et de temps.</span>
            </p>
          </div>
        </div>

        <div className="ps-cta-wrapper">
          <div className="ps-cta-card">
            <div className="ps-cta-glow"></div>
            
            <div className="ps-cta-content">
              <div className="ps-cta-icon-wrapper">
                <FaInfinity className="ps-cta-icon" />
              </div>
              
              <h3 className="ps-cta-title">
                Ne laissez plus <span className="ps-cta-gradient">vos prospects</span> vous échapper
              </h3>
              
              <p className="ps-cta-text">
                Discutons de votre système de suivi actuel et voyons ensemble comment nous pouvons l'optimiser.
              </p>
              
              <div className="ps-cta-benefits">
                <div className="ps-benefit">
                  <MdCheckCircle />
                  <span>Audit gratuit</span>
                </div>
                <div className="ps-benefit">
                  <MdInsights />
                  <span>Diagnostic complet</span>
                </div>
                <div className="ps-benefit">
                  <MdTimeline />
                  <span>Solution sur mesure</span>
                </div>
              </div>
            </div>
            
            <motion.button 
              className="ps-cta-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Optimiser mon suivi</span>
              <MdArrowForward className="ps-cta-button-icon" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;